import { Controller } from '@hotwired/stimulus'

// Reveals elements on scroll using IntersectionObserver.
//
// Usage:
//   <div data-controller="scroll-reveal">
//     <div data-scroll-reveal-target="item" class="reveal">...</div>
//     <div data-scroll-reveal-target="item" class="reveal">...</div>
//   </div>
//
// Each item gets the `is-visible` class when it enters the viewport. Items are
// staggered automatically by their index via the `--reveal-index` CSS variable.
// If the controller never connects (no JS), CSS keeps the content visible.
export default class extends Controller {
  static targets = ['item']
  static values = {
    threshold: { type: Number, default: 0.12 },
    once: { type: Boolean, default: true }
  }

  connect() {
    // Bail out gracefully if the browser can't observe or the user prefers no motion.
    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      this.itemTargets.forEach((item) => item.classList.add('is-visible'))
      return
    }

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      threshold: this.thresholdValue,
      rootMargin: '0px 0px -8% 0px'
    })

    this.itemTargets.forEach((item, index) => {
      item.style.setProperty('--reveal-index', index)
      this.observer.observe(item)
    })
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible')
        if (this.onceValue) this.observer.unobserve(entry.target)
      } else if (!this.onceValue) {
        entry.target.classList.remove('is-visible')
      }
    })
  }
}
